import { Icon } from '@buggy/shared'
import { Button, Flex, Modal, Spin, Typography, notification } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { z } from 'zod'
import {
  useDeleteCustomizedLabelMutation,
  useEditCustomizedLabelMutation,
} from '@/api/workspace-api/customized-labels-api'
import {
  useCreateCustomizedLabelMutation,
  useFetchCustomizedLabelsQuery,
} from '@/api/workspace-api/directory-api'
import BasicInput from '@/components/common/antd/basic-input'
import BasicSelect from '@/components/common/antd/basic-select'
import DeleteButton from '@/components/common/buttons/delete-button'
import HoverableElement from '@/components/common/hoverable-element'
import SingleInputFieldModal from '@/components/common/modals/single-input-field-modal'
import { NoData } from '@/components/kol/detail/no-data'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { FlattenOptionData } from '@/types/antd/select'
import { type CustomizedLabel } from '@/types/schema/directory/kol-management'
import getCustomizedLabelLocalizedName, {
  BUILT_IN_CUSTOMIZED_LABEL_AMOUNT,
  BUILT_IN_CUSTOMIZED_LABEL_IDS,
} from '@/utils/directory/get-customized-label-localized-name'
import logger from '@/utils/logger'

const CUSTOMIZED_LABELS_MAXIMUM_LENGTH = 50 as const

interface OptionType extends DefaultOptionType {
  customizedLabel: CustomizedLabel
}

interface CustomizedLabelsSelectProps {
  value?: CustomizedLabel[]
  maxCreationCount?: number
  editable?: boolean
  onChange?: (customizedLabel?: CustomizedLabelsSelectProps['value']) => void
}

const CustomizedLabelsSelect: FunctionComponent<
  CustomizedLabelsSelectProps
> = ({ value, onChange, maxCreationCount, editable }) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const [uncommittedCustomizedLabelName, setUncommittedCustomizedLabelName] =
    useState<string>()
  const [
    uncommittedCustomizedLabelNameResult,
    setUncommittedCustomizedLabelNameResult,
  ] = useState<z.SafeParseReturnType<string, string>>()
  const [editingCustomizedLabel, setEditingCustomizedLabel] =
    useState<CustomizedLabel>()

  const { data, isFetching } = useFetchCustomizedLabelsQuery()
  const [createCustomizedLabel, { isLoading: isCreatingCustomizedLabel }] =
    useCreateCustomizedLabelMutation()
  const [editCustomizedLabel] = useEditCustomizedLabelMutation()
  const [deleteCustomizedLabel] = useDeleteCustomizedLabelMutation()

  const options = useMemo((): OptionType[] => {
    if (!data) {
      return []
    }

    return data.data.customized_labels.map((customizedLabel) => ({
      label: getCustomizedLabelLocalizedName(customizedLabel, formatMessage),
      value: customizedLabel.id,
      customizedLabel,
    }))
  }, [data, formatMessage])
  const customizedLabelNameSchema = z
    .string()
    .regex(
      /^[\p{L}\p{N}]+$/u,
      formatMessage(
        { id: 'error:incorrect_format' },
        { inputField: formatMessage({ id: 'irm:community_filter_tag' }) },
      ),
    )
    .max(
      CUSTOMIZED_LABELS_MAXIMUM_LENGTH,
      formatMessage(
        { id: 'error:field_max_length' },
        {
          inputField: formatMessage({ id: 'irm:community_filter_tag' }),
          length: CUSTOMIZED_LABELS_MAXIMUM_LENGTH,
        },
      ),
    )
    .refine(
      (val) => !options.some((option) => option.label === val),
      (val) => {
        return {
          message: formatMessage(
            { id: 'general:duplicated_add' },
            { resource: val },
          ),
        }
      },
    )

  const handleCreateCustomizedLabel = async (): Promise<void> => {
    if (!uncommittedCustomizedLabelName || !maxCreationCount) {
      return
    }

    const parseResult = customizedLabelNameSchema.safeParse(
      uncommittedCustomizedLabelName,
    )

    setUncommittedCustomizedLabelNameResult(parseResult)

    if (options.length - BUILT_IN_CUSTOMIZED_LABEL_AMOUNT >= maxCreationCount) {
      Modal.confirm({
        centered: true,
        title: formatMessage({
          id: 'directory:customized_labels_reach_limit_title',
        }),
        content: formatMessage(
          {
            id: 'directory:customized_labels_reach_limit_content',
          },
          { count: maxCreationCount },
        ),
        okButtonProps: {
          style: {
            background: theme.colors.plan.premium,
          },
          icon: (
            <Icon
              fill={theme.colors.text.white}
              fontSize={16}
              name='crown-outlined'
            />
          ),
        },
        okText: formatMessage({
          id: 'vip:upgrade_plan_btn',
        }),
        cancelText: formatMessage({ id: 'general:btn_text_i_see' }),
      })
      return
    }

    if (parseResult.error) {
      return
    }

    try {
      await createCustomizedLabel(parseResult.data)
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({ id: 'general:operation_failure' }),
        placement: 'bottomRight',
      })
    }

    setUncommittedCustomizedLabelName(undefined)
  }

  return (
    <>
      <BasicSelect
        allowClear
        dropdownRender={(menu) => (
          <>
            {menu}
            {!!maxCreationCount && data && (
              <CreateForm>
                <Flex gap={8}>
                  <BasicInput
                    allowClear
                    count={{
                      show: true,
                      max: CUSTOMIZED_LABELS_MAXIMUM_LENGTH,
                    }}
                    disabled={isCreatingCustomizedLabel}
                    placeholder={formatMessage({
                      id: 'kol:edit_coop_field_content_placeholder',
                    })}
                    size='small'
                    value={uncommittedCustomizedLabelName}
                    onChange={(event) => {
                      setUncommittedCustomizedLabelNameResult(undefined)
                      setUncommittedCustomizedLabelName(event.target.value)
                    }}
                  />
                  <Button
                    danger
                    disabled={
                      !uncommittedCustomizedLabelName ||
                      uncommittedCustomizedLabelName.length === 0
                    }
                    icon={<Icon name='plus' />}
                    loading={isCreatingCustomizedLabel}
                    size='small'
                    onClick={handleCreateCustomizedLabel}
                  >
                    {formatMessage({ id: 'kol:edit_field_new_tag' })}
                  </Button>
                </Flex>
                {uncommittedCustomizedLabelNameResult?.error?.issues[0]
                  ?.message && (
                  <Typography.Text type='danger'>
                    {
                      uncommittedCustomizedLabelNameResult.error.issues[0]
                        .message
                    }
                  </Typography.Text>
                )}
              </CreateForm>
            )}
          </>
        )}
        loading={isFetching}
        mode='multiple'
        notFoundContent={
          isFetching ? <Spin style={{ width: '100%' }} /> : <NoData />
        }
        optionFilterProp='label'
        optionRender={(option: FlattenOptionData<OptionType>) => (
          <HoverableElement>
            {(hovered) => {
              return (
                <Flex align='center' gap={8}>
                  {option.label}
                  {hovered &&
                    editable &&
                    !BUILT_IN_CUSTOMIZED_LABEL_IDS.includes(
                      option.data.customizedLabel.id,
                    ) && (
                      <ActionButtons>
                        <Button
                          icon={
                            <Icon
                              color={theme.colors.brand.secondary}
                              fontSize={16}
                              name='edit-3'
                            />
                          }
                          type='text'
                          onClick={(event) => {
                            event.stopPropagation()
                            setEditingCustomizedLabel(
                              option.data.customizedLabel,
                            )
                          }}
                        />
                        <DeleteButton
                          confirmModalContent={formatMessage({
                            id: 'customized_labels:confirm_delete_description',
                          })}
                          confirmModalTitle={formatMessage(
                            {
                              id: 'customized_labels:confirm_delete_title',
                            },
                            { labelName: option.label },
                          )}
                          type='text'
                          onDelete={async () => {
                            try {
                              await deleteCustomizedLabel(
                                option.data.customizedLabel.id,
                              ).unwrap()
                            } catch (error) {
                              logger.error(error)
                            }
                          }}
                        />
                      </ActionButtons>
                    )}
                </Flex>
              )
            }}
          </HoverableElement>
        )}
        options={options}
        placeholder={formatMessage({ id: 'general:select_placeholder' })}
        value={value?.map((customizedLabel) => customizedLabel.id)}
        onChange={(_newValue, options: OptionType[]): void => {
          onChange?.(
            isEmpty(options)
              ? undefined
              : options.map((option) => option.customizedLabel),
          )
        }}
      />
      {editingCustomizedLabel && (
        <SingleInputFieldModal
          open
          required
          description={
            <EditModalDescription>
              {formatMessage({
                id: 'customized_labels:edit_modal_description',
              })}
            </EditModalDescription>
          }
          fieldLabel={formatMessage({ id: 'general:customized_label_name' })}
          getContainer={() => document.body}
          rules={[
            {
              validateTrigger: 'onSubmit',
              validator: (_, value): Promise<void> => {
                try {
                  customizedLabelNameSchema.parse(value)
                  return Promise.resolve()
                } catch (error) {
                  if (error instanceof z.ZodError) {
                    return Promise.reject(new Error(error.errors[0].message))
                  }
                  return Promise.reject(
                    new Error(
                      formatMessage({ id: 'general:operation_failure' }),
                    ),
                  )
                }
              },
            },
          ]}
          title={formatMessage({ id: 'irm:edit_customized_tag' })}
          value={editingCustomizedLabel.name}
          onClose={() => setEditingCustomizedLabel(undefined)}
          onSave={async (value): Promise<void> => {
            try {
              await editCustomizedLabel({
                id: editingCustomizedLabel.id,
                customized_label: value,
              })
            } catch (error) {
              logger.error(error)

              notification.error({
                message: formatMessage({ id: 'general:operation_failure' }),
                placement: 'bottomRight',
              })
            }
          }}
        />
      )}
    </>
  )
}

const CreateForm = styled.div`
  margin: 4px 0 0;
  display: flex;
  flex-direction: column;
  padding: 8px 8px 4px;
  gap: 4px;
  border-top: 1px solid ${({ theme }): string => theme.colors.border.divider};
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  .ant-btn.ant-btn-icon-only:not(.ant-btn-sm):not(.ant-btn-block) {
    font-size: 14px;
    width: 16px;
    height: 16px;
    padding: 0;

    &:not(:disabled) {
      color: ${({ theme }): string => theme.colors.brand.secondary};

      &:hover {
        color: ${({ theme }): string => theme.colors.brand.secondaryHover};
        background: transparent;
      }
    }

    .ant-btn-icon {
      svg {
        width: 16px;
        height: 16px;
        vertical-align: unset;
      }
    }
  }
`

const EditModalDescription = styled(Typography.Text)`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

export default CustomizedLabelsSelect
